import React, { useState } from 'react';
import { Briefcase, Calendar, ChevronRight, ChevronDown } from 'lucide-react';
import { Card } from './ui/Card';
interface CareerItem {
  company: string;
  role: string;
  duration: string;
  location: string;
  achievements: string[];
}
const careerHistory: CareerItem[] = [{
  company: 'SAP Concur',
  role: 'Technical Program Manager - AI Transformation',
  duration: 'Oct 2024 – Present',
  location: 'Prague, CZ',
  achievements: ['AI & Automation: Implemented AI-driven automation reducing manual processing by 40% and accelerating deployment cycles by 30%, saving significant FTE costs through custom AI agents', 'Process Improvement: Streamlined enterprise-wide security procedures by 25% through business process analysis and optimization aligned with global operating model', 'Data Analysis: Managed PCI attestations and pentest remediation achieving 95% resolution within SLA; onboarded 3+ SIEM log sources']
}, {
  company: 'Médecins Sans Frontières (MSF)',
  role: 'Security Analyst - International Operations',
  duration: 'Sep 2023 – Sep 2024',
  location: 'Prague, CZ',
  achievements: ['Cross-Border Coordination: Coordinated security operations and incident response across 70+ countries for humanitarian operations', 'Data Analysis: Utilized MS Sentinel SIEM to collect, analyze, and interpret security data, reducing average response time through workflow optimization', 'Business-IT Bridge: Translated field operations security needs into technical system requirements while maintaining global compliance']
}, {
  company: 'Pinkerton',
  role: 'Regional Security Program Operator',
  duration: 'Nov 2022 – Aug 2023',
  location: 'Prague, CZ',
  achievements: ['Process Improvement: Managed Identity & Access Management for 1,000+ personnel, reducing security incidents by 20% through workflow optimization', 'Data Analysis: Executed OSINT collection and analysis on 30+ targets achieving 98% accuracy in risk assessment using proprietary software', 'Operations Management: Coordinated security operations protecting 500+ assets worth $1B+ across 57 locations covering 1M+ sq. ft.']
}, {
  company: 'Larsen & Toubro Defence',
  role: 'Senior Engineer, International Defence Business Development',
  duration: 'Jul 2018 – Jul 2020',
  location: 'Mumbai, IN',
  achievements: ['Requirements & Specifications: Drafted responses to government/UN RFPs and tenders for $1B+ defense systems, translating technical capabilities into business proposals', 'Stakeholder Management: Engaged with 7 defense ministers and 200+ industry stakeholders; managed relationships with 4 channel partners and 7 vendors', 'Business Analysis: Synthesized strategies across engineering, finance, product, and marketing; conducted competitive landscape analysis and ROI modeling']
}];
export function CareerTimeline() {
  const [expandedItems, setExpandedItems] = useState<number[]>([0]); // First item expanded by default
  const toggleItem = (index: number) => {
    setExpandedItems(prev => prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]);
  };
  return <div className="relative space-y-8 before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-border before:to-transparent" data-id="element-1">
      {careerHistory.map((item, index) => {
      const isExpanded = expandedItems.includes(index);
      return <div key={index} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-2">
            {/* Icon */}
            <div className="flex items-center justify-center w-10 h-10 rounded-full border border-border bg-background shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-3">
              <Briefcase className="w-5 h-5 text-primary" data-id="element-4" />
            </div>

            {/* Card */}
            <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] p-4" data-id="element-5">
              <Card className="hover:shadow-lg transition-all duration-300 border-l-4 border-l-primary/50 overflow-hidden" data-id="element-6">
                {/* Clickable Header */}
                <button onClick={() => toggleItem(index)} className="w-full p-6 text-left hover:bg-secondary/30 transition-colors duration-200" data-id="element-7">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between" data-id="element-8">
                    <div className="flex-1" data-id="element-9">
                      <div className="flex items-start justify-between gap-4" data-id="element-10">
                        <div className="flex-1" data-id="element-11">
                          <h3 className="font-bold text-xl text-foreground leading-tight mb-2" data-id="element-12">
                            {item.role}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 text-sm" data-id="element-13">
                            <p className="text-primary font-semibold" data-id="element-14">
                              {item.company}
                            </p>
                            <span className="text-muted-foreground" data-id="element-15">•</span>
                            <p className="text-muted-foreground" data-id="element-16">
                              {item.location}
                            </p>
                          </div>
                        </div>
                        <ChevronDown className={`w-5 h-5 text-primary shrink-0 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-17" />
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center text-sm text-muted-foreground mt-3 bg-secondary/50 px-3 py-1.5 rounded-full w-fit" data-id="element-18">
                    <Calendar className="w-4 h-4 mr-2" data-id="element-19" />
                    {item.duration}
                  </div>
                </button>

                {/* Expandable Content */}
                <div className={`transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-20">
                  <div className="px-6 pb-6" data-id="element-21">
                    <ul className="space-y-3 pt-4 border-t border-border/50" data-id="element-22">
                      {item.achievements.map((achievement, i) => {
                    const parts = achievement.split(': ');
                    const hasTitle = parts.length > 1;
                    return <li key={i} className="flex items-start text-sm" data-id="element-23">
                            <ChevronRight className="w-4 h-4 mr-2 text-primary shrink-0 mt-0.5" data-id="element-24" />
                            <span className="text-muted-foreground" data-id="element-25">
                              {hasTitle ? <>
                                  <strong className="text-foreground font-semibold" data-id="element-26">
                                    {parts[0]}:
                                  </strong>{' '}
                                  {parts.slice(1).join(': ')}
                                </> : achievement}
                            </span>
                          </li>;
                  })}
                    </ul>
                  </div>
                </div>
              </Card>
            </div>
          </div>;
    })}
    </div>;
}